import React, { useState, useEffect, useRef } from 'react';
import { ui } from '@owenscorning/pcb.alpha';
import styled from '@emotion/styled';
import { connectLocation } from '../../../../location/LocationConnectors';
import { truncateText } from '../../../../../helpers/text_truncate_helper';
import Theme from '../../../../../themes/index';
import RichText from '../../../../OC/PageBuilder/RichText';
import Text from '../../../../OC/PageBuilder/utilities/Text';
import { H4 } from '../../../../OC/oc-h';
import Card from '../../../../OC/oc-card-new';
import { H3 } from '../../../../ComponentLibrary/text-elements/oc-h';
import { DefaultTextInput } from '../../../../forms/OCInputs';
import Cta from '../../../../OC/oc-cta';

// =====================
// Styled Components
// =====================
const Container = styled.div`
  width: 100%;
  margin-bottom: 2rem;
  &:focus {
    outline: none;
  }
  &:hover {
    h4 {
      color: ${Theme.colors.brand};
    }
  }
`;

const ModalCard = styled(Card)`
  > div:first-child {
  }
`;

const Prehead = styled.span`
  color: #6d6d6d;
  font-weight: normal;
  font-family: ${Theme.fonts.body};
  margin-bottom: 4px;
  letter-spacing: 0;
`;

const BadgeItem = styled.span`
  background-color: #e6e6e6;
  color: black;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  padding: 3px 6px;
  border-radius: 15px;
`;

const CtaButton = styled.div`
  display: block;
  margin-top: 1rem;
`;

const TextInput = styled(DefaultTextInput)`
  padding: 16px 8px;
  border: 1px solid #585858;
  width: 100%;
`;

const InputWrapper = styled.div`
  margin-bottom: 1rem;
  span {
    color: #d9534f;
  }
`;

// Helper function to remove img tags from HTML string
const removeImgTags = htmlString => {
  if (!htmlString) return '';
  return htmlString.replace(/<img[^>]*>/gi, '');
};

const Hr = styled.hr`
  margin: 2rem 0;
`;

const Button = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

// =====================
// Constants
// =====================
/**
 * RFC 5322 Official Standard Email Regex
 * Accepts most valid email addresses, including subdomains and country TLDs
 */
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@\"]+\.)+[^<>()[\]\\.,;:\s@\"]{2,})$/i;

// =====================
// Helper Functions
// =====================
/**
 * Validates an email address using EMAIL_REGEX
 * @param {string} email
 * @returns {boolean}
 */
function isValidEmail(email) {
  return EMAIL_REGEX.test(email);
}

/**
 * Modal body for requesting email and handling OCConnect logic
 */
function RegisterModalBody({ event, t }) {
  const { id, name, url } = event;
  const [email, setEmail] = useState('');
  const [connectUser, setConnectUser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const registerUrl = `${url}/begin`;

  /**
   * Handles email submission and OCConnect lookup
   */
  const handleEmailSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!email || !isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${MDMS_URL}/api/v1/contractor/users/active-user?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.result === 'found') {
        setConnectUser(true);
      } else if (data.result === 'not_found') {
        Board.modal.close();
        window.open(registerUrl, '_blank');
      } else {
        setError('An error occurred while checking your email');
      }
    } catch (err) {
      Board.modal.close();
      window.open(registerUrl, '_blank');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalCard transform={null} aspectRatio={{ selection: 'none' }}>
      <H3 font="body">{name}</H3>
      {connectUser ? (
        <OcConnectBody id={id} t={t} />
      ) : (
        <RequestEmailBody
          email={email}
          setEmail={setEmail}
          handleEmailSubmit={handleEmailSubmit}
          isLoading={isLoading}
          error={error}
          t={t}
        />
      )}
    </ModalCard>
  );
}

/**
 * Email input and submit UI
 */
function RequestEmailBody({ email, setEmail, handleEmailSubmit, isLoading, error, t }) {
  const handleSubmit = e => {
    e.preventDefault();
    if (!isLoading) {
      handleEmailSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <RichText content={t('connect.event.proceed_to_enter_email')} />
      <InputWrapper>
        <TextInput
          placeholder="Email"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          onKeyPress={e => {
            if (e.key === 'Enter' && !isLoading) {
              handleEmailSubmit(e);
            }
          }}
          required
          disabled={isLoading}
          autoFocus
        />
        {error && <span className="error">{error}</span>}
      </InputWrapper>
      <Button type="submit">
        <Cta disabled={isLoading}>{isLoading ? 'Checking...' : t('connect.btn.submit')}</Cta>
      </Button>
    </form>
  );
}

/**
 * OCConnect login prompt UI
 */
function OcConnectBody({ id, t }) {
  const buttonRef = useRef(null);

  useEffect(() => {
    // Focus the button when component mounts
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []);

  return (
    <div>
      <RichText content={t('connect.event.connect_account')} />
      <Button
        ref={buttonRef}
        autoFocus
        onKeyPress={e => {
          if (e.key === 'Enter') {
            window.location.href = `/connect/learning/owens-corning-university/events/${id}`;
          }
        }}
      >
        <Cta href={`/connect/learning/owens-corning-university/events/${id}`} data-track="learn-more">
          {t('connect.btn.login_to_connect')}
        </Cta>
      </Button>
    </div>
  );
}

/**
 * Main SwoogoEvents event card component
 */
const SwoogoEvents = connectLocation(({ aspectRatio, locale: { code }, t, ...event }) => {
  // Safety check for event object
  if (!event || !event.name) {
    return <Container>{t('connect.event.data_not_available')}</Container>;
  }
  return (
    <Container data-track="view-event" data-event-name={event.name} title={event.name}>
      <div
        style={{ cursor: 'pointer' }}
        onClick={e => {
          e.preventDefault();
          Board.modal.open({
            body: () => <RegisterModalBody event={event} t={t} />,
            whiteCloseButton: false,
          });
        }}
      >
        <H4 tag="h4" font="body" style={{ color: '#000' }}>
          <Text content={event.name} />
        </H4>
      </div>
      <Prehead>
        {event.start_end_date}
        {' • '}
        {event.start_time} - {event.end_time} {event.timezone?.split('/').pop()} • {event.location?.name},{' '}
        {event.location?.city}, {event.location?.state}
      </Prehead>
      <RichText content={truncateText(removeImgTags(event.description) || '', 320)} />
      <BadgeItem>{event.venue_type}</BadgeItem>
      <CtaButton>
        <Cta
          href={event.url}
          onClick={e => {
            e.preventDefault();
            Board.modal.open({
              body: () => <RegisterModalBody event={event} t={t} />,
              whiteCloseButton: false,
            });
          }}
          target="_blank"
        >
          {t('connect.btn.view_details_and_register')}
        </Cta>
      </CtaButton>
      <Hr />
    </Container>
  );
});

export default SwoogoEvents;
